import React, { useEffect, useState } from 'react';
import {Box, Divider, Typography, WordCountArea} from 'web-library';
import { FormControlLabel, Radio, RadioGroup } from '@mui/material';

import { makeStyles } from 'tss-react/mui';
import {Control, UseFormReturn, UseFormSetValue} from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import ControlledCustomSelect from '../../../../../components/form-items/ControlledCustomSelect';
import SubjectPaperDataTable from './SubjectPaperDataTable';
import {
  AccessArrangementRequestData,
  AccessArrangementRequestSelection
} from './AccessArrangementRequestModels';

const useStyles = makeStyles()((theme) =>
({
  spaceBottom: {
    marginBottom: theme.spacing(3),
  },
  radioGroupRoot: {
    marginLeft: theme.spacing(2),
    display: 'flex',
    justifyContent: 'space-between',
  },
  remarks: {
    marginBottom: theme.spacing(2),
  },
  minHeight: {
    minHeight: 125,
  },
}));

interface ModalContentProps {
  accessArrangementRequestSelections: AccessArrangementRequestSelection;
  remarksMaxLength: number;
  control: Control<AccessArrangementRequestData>;
  form: UseFormReturn<AccessArrangementRequestData>;
  setValue: UseFormSetValue<AccessArrangementRequestData>;
  watch: (name: string) => any;
  getRemarkIndicator: (remarkIndicator: boolean) => void;
}

const AddRequestModalContent = ({
  accessArrangementRequestSelections,
  remarksMaxLength,
  control,
  setValue,
  watch,
  getRemarkIndicator,
                                  form
}: ModalContentProps) => {
  const { t } = useTranslation();
  const { classes } = useStyles();
  const { formState: {errors} } = form;
  const [modeOfAssessment, setModeOfAssessment] = useState('');
  const [accessArrangementTypes, setAccessArrangementTypes] = useState<any>([]);
  const [subjectPapersData, setSubjectPapersData] = useState<any>([]);
  const [remarkIndicators, setRemarkIndicators] = useState<{
    [requestTypeCode: string]: boolean;
  }>();
  const [remarkIndicator, setRemarkIndicator] = useState(false);

  const requestTypeChange = watch('requestTypeUuid');

  useEffect(() => {
    if (remarkIndicators) {
      const indicator = remarkIndicators[requestTypeChange];

      if (indicator !== remarkIndicator) {
        setRemarkIndicator(indicator);
        getRemarkIndicator(indicator);
      }
    }
  }, [remarkIndicators, remarkIndicator, requestTypeChange, remarksMaxLength, getRemarkIndicator]);

  useEffect(()=>{
    setValue("remarks", '')
  },[requestTypeChange])
  return (
    <>
      <Typography className={classes.spaceBottom} variant="h3">
        {t('accessArrangement->>AccessArrangementRequest.Add.ModeOfAssessment')}
      </Typography>
      <Typography className={classes.spaceBottom} variant="body1">
        {t('accessArrangement->>AccessArrangementRequest.ModeOfAssessmentDescription')}
      </Typography>
      <RadioGroup
        aria-label="modeOfAssessment"
        name="modeOfAssessment"
        className={`${classes.radioGroupRoot} ${classes.spaceBottom}`}
        value={modeOfAssessment}
        onChange={({ target: { value } }) => {
          setModeOfAssessment(value);
          setValue('requestTypeUuid', undefined);
          const accessArrangementRequestSelected = accessArrangementRequestSelections[value];

          if (accessArrangementRequestSelected) {
            setAccessArrangementTypes([...accessArrangementRequestSelected.accessArrangementTypes]);
            setSubjectPapersData([...accessArrangementRequestSelected.subjectPapers]);
          }

          setRemarkIndicators(accessArrangementRequestSelections[value].remarkIndicators);
        }}
      >
        {Object.keys(accessArrangementRequestSelections).map((key, index) => (
          <FormControlLabel
            id={`radioButton-accessArrangementRequest-${index}`}
            key={key}
            value={key}
            control={<Radio />}
            label={accessArrangementRequestSelections[key].modeOfAssessmentDescription}
          />
        ))}
      </RadioGroup>

      {modeOfAssessment && (
        <Box key={modeOfAssessment}>
          <Divider />
          <Typography className={classes.spaceBottom} variant="h3">
            {t('accessArrangement->>AccessArrangementRequest.Add.AccessArrangementRequestType')}
          </Typography>

          <ControlledCustomSelect
              id="dropdown-requestType"
              label={t('accessArrangement->>AccessArrangementRequest.AccessArrangementType')}
              name="requestTypeUuid"
              control={control}
              menuItems={accessArrangementTypes}
              className={classes.spaceBottom}
              displayEmpty
          />

          {requestTypeChange && (
            <Box>
              {accessArrangementRequestSelections[modeOfAssessment].remarkIndicators[
                requestTypeChange
              ] && (
                  <WordCountArea
                      form={form}
                      name="remarks"
                      className={classes.remarks}
                      error={!!errors.remarks}
                      label={t('accessArrangement->>AccessArrangementRequest.Remarks')}
                      placeholder={t('accessArrangement->>AccessArrangementRequest.RemarksPlaceholder')}
                      type="text"
                      maxLength={remarksMaxLength}
                      minRows={3}
                      // CPEPOKPNEH-3391
                      onChange={(e) => {
                          const trimmedValue = e.target.value.slice(0, remarksMaxLength); // Enforce dynamic maxLength
                          form.setValue('remarks', trimmedValue);
                      }}
                  />
                )}
              <Box key={requestTypeChange as string}>
                <Divider />
                <Typography className={classes.spaceBottom} variant="h3">
                  {t('accessArrangement->>AccessArrangementRequest.SubjectAndPaper')}
                </Typography>
                <Typography className={classes.spaceBottom} variant="body1">
                  {t('accessArrangement->>AccessArrangementRequest.SubjectAndPaperDescription')}
                </Typography>

                <Box className={classes.spaceBottom}>
                  <SubjectPaperDataTable
                    control={control}
                    setValue={setValue}
                    subjectPapersData={subjectPapersData}
                    modeOfAssessment={modeOfAssessment}
                    requestTypeChange={requestTypeChange}
                  />
                </Box>
              </Box>
            </Box>
          )}
        </Box>
      )}
    </>
  );
};

export default AddRequestModalContent;
